import * as React from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    Link,
    TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {sendPasswordResetEmail} from 'firebase/auth';
import {useAuth} from '../../auth/AuthContext';
import {auth} from '../../firebase/firebase';
import {GoogleLogo} from './GoogleLogo';
import styles from './LoginDialog.module.scss';

type Mode = 'signIn' | 'register';

interface LoginDialogProps {
    open: boolean;
    onClose: () => void;
    reason?: string;
}

export const LoginDialog: React.FC<LoginDialogProps> = ({open, onClose, reason}) => {
    const {signInWithGoogle, signInWithEmail, registerWithEmail} = useAuth();
    const [mode, setMode] = React.useState<Mode>('signIn');
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [displayName, setDisplayName] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [info, setInfo] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (open) {
            setMode('signIn');
            setEmail('');
            setPassword('');
            setDisplayName('');
            setError(null);
            setInfo(null);
            setBusy(false);
        }
    }, [open]);

    const run = async (fn: () => Promise<void>) => {
        setBusy(true);
        setError(null);
        setInfo(null);
        try {
            await fn();
        } catch (e) {
            setError(friendlyError(e));
        } finally {
            setBusy(false);
        }
    };

    const handleGoogle = () => run(signInWithGoogle);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (mode === 'signIn') {
            run(() => signInWithEmail(email.trim(), password));
        } else {
            run(() => registerWithEmail(email.trim(), password, displayName.trim() || undefined));
        }
    };

    const handleForgot = async () => {
        const target = email.trim();
        if (!target) {
            setError('Enter your email first, then click "Forgot password".');
            return;
        }
        await run(async () => {
            await sendPasswordResetEmail(auth, target);
            setInfo(`Password reset email sent to ${target}.`);
        });
    };

    const title = mode === 'signIn' ? 'Welcome back' : 'Create your account';
    const subtitle = reason
        ? `Sign in to ${reason}.`
        : mode === 'signIn'
            ? 'Sign in to sync your playlists across devices.'
            : 'Join KeePlaying to save and sync your playlists.';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <div className={styles.loginDialog}>
                <IconButton className={styles.loginDialog__close} onClick={onClose} aria-label="Close">
                    <CloseIcon/>
                </IconButton>

                <div className={styles.loginDialog__header}>
                    <div className={styles.loginDialog__title}>{title}</div>
                    <div className={styles.loginDialog__subtitle}>{subtitle}</div>
                </div>

                <DialogContent className={styles.loginDialog__content}>
                    <Button
                        className={styles.loginDialog__googleBtn}
                        variant="outlined"
                        fullWidth
                        disabled={busy}
                        onClick={handleGoogle}
                        startIcon={<GoogleLogo/>}
                    >
                        Continue with Google
                    </Button>

                    <Divider className={styles.loginDialog__divider}>or</Divider>

                    <form className={styles.loginDialog__form} onSubmit={handleSubmit}>
                        {mode === 'register' && (
                            <TextField
                                label="Display name (optional)"
                                variant="filled"
                                fullWidth
                                value={displayName}
                                onChange={e => setDisplayName(e.target.value)}
                                disabled={busy}
                            />
                        )}
                        <TextField
                            label="Email"
                            type="email"
                            variant="filled"
                            fullWidth
                            required
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            disabled={busy}
                            autoComplete="email"
                        />
                        <TextField
                            label="Password"
                            type="password"
                            variant="filled"
                            fullWidth
                            required
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            disabled={busy}
                            autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'}
                            helperText={mode === 'register' ? 'At least 6 characters.' : undefined}
                        />

                        {error && <Alert severity="error">{error}</Alert>}
                        {info && <Alert severity="success">{info}</Alert>}

                        <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            fullWidth
                            disabled={busy || !email.trim() || !password}
                            className={styles.loginDialog__submit}
                        >
                            {mode === 'signIn' ? 'Sign in' : 'Create account'}
                        </Button>

                        {mode === 'signIn' && (
                            <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={handleForgot}
                                className={styles.loginDialog__forgot}
                            >
                                Forgot password?
                            </Link>
                        )}
                    </form>

                    <div className={styles.loginDialog__switch}>
                        {mode === 'signIn' ? (
                            <>
                                New here?{' '}
                                <Link component="button" type="button" onClick={() => setMode('register')}>
                                    Create an account
                                </Link>
                            </>
                        ) : (
                            <>
                                Already have an account?{' '}
                                <Link component="button" type="button" onClick={() => setMode('signIn')}>
                                    Sign in
                                </Link>
                            </>
                        )}
                    </div>
                </DialogContent>
            </div>
        </Dialog>
    );
};

// Translate the common Firebase auth error codes into human text.
// All other errors fall through to the raw message.
function friendlyError(err: unknown): string {
    const code = (err as {code?: string})?.code;
    switch (code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
        case 'auth/user-not-found':
            return 'Email or password is incorrect.';
        case 'auth/email-already-in-use':
            return 'An account with this email already exists. Try signing in instead.';
        case 'auth/invalid-email':
            return 'That doesn\'t look like a valid email.';
        case 'auth/weak-password':
            return 'Password should be at least 6 characters.';
        case 'auth/popup-closed-by-user':
            return 'Sign-in window was closed before finishing.';
        case 'auth/network-request-failed':
            return 'Network error. Check your connection and try again.';
        case 'auth/too-many-requests':
            return 'Too many attempts. Please wait a moment and try again.';
        case 'GOOGLE_NO_ELIGIBLE_ACCOUNT':
            return 'Couldn\'t sign in with Google on this device. Please use Email below, or check that a Google account is added in Settings → Accounts.';
        default:
            return (err as Error)?.message || 'Something went wrong. Please try again.';
    }
}
